import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getPhoneCallIdToDisplay, shouldCleanUpUrlOnPhoneCallChange } from '../utils/phoneCallUrl';

/**
 * Manages phone call display state from URL parameters.
 *
 * Handles two scenarios:
 * - Notification link: Uses URL param to show the correct phone call
 * - Button click: Uses prop to show phone call and cleans up stale URL params
 *
 * @param initialPhoneCallId - The phone call ID from props
 * @returns Object with phone call ID to display and URL cleanup function
 */
export const usePhoneCallFromUrl = (initialPhoneCallId: number) => {
    const navigate = useNavigate();
    const location = useLocation();
    const prevPhoneCallIdRef = useRef<number>(initialPhoneCallId);

    const { urlPhoneCallId, fromNotification, phoneCallIdToDisplay } = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlPhoneCallId = searchParams.get('phoneCall');
        const fromNotification = searchParams.get('fromNotification') === 'true';
        const phoneCallIdToDisplay = getPhoneCallIdToDisplay(
            urlPhoneCallId,
            fromNotification,
            initialPhoneCallId,
        );

        return { urlPhoneCallId, fromNotification, phoneCallIdToDisplay };
    }, [location.search, initialPhoneCallId]);

    const cleanUpUrlParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);

        if (searchParams.has('phoneCall') || searchParams.has('fromNotification')) {
            searchParams.delete('phoneCall');
            searchParams.delete('fromNotification');
            const newSearch = searchParams.toString();
            navigate(
                {
                    pathname: location.pathname,
                    search: newSearch ? `?${newSearch}` : '',
                },
                { replace: true },
            );
        }
    }, [navigate, location.pathname, location.search]);

    useEffect(() => {
        if (prevPhoneCallIdRef.current !== initialPhoneCallId) {
            if (shouldCleanUpUrlOnPhoneCallChange(urlPhoneCallId, initialPhoneCallId)) {
                cleanUpUrlParams();
            }

            prevPhoneCallIdRef.current = initialPhoneCallId;
        }
    }, [cleanUpUrlParams, initialPhoneCallId, urlPhoneCallId]);

    return {
        urlPhoneCallId,
        fromNotification,
        phoneCallIdToDisplay,
        cleanUpUrlParams,
    };
};
